import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HEALTH_CONDITIONS = [
  "Pregnancy", "Diabetes", "High blood pressure", "Heart condition",
  "Autoimmune disorder", "Cancer (past or present)", "Eczema", "Psoriasis",
  "Cold sores / Herpes simplex", "Keloid scarring", "Thyroid condition", "Hormonal imbalance"
];

const MEDICATIONS = [
  "Retin-A / Tretinoin", "Accutane (within past 12 months)", "Antibiotics",
  "Blood thinners", "Steroids"
];

const RECENT_TREATMENTS = [
  "Retinol", "Chemical exfoliants (AHA/BHA)", "Chemical peel",
  "Waxing", "Laser treatment"
];

const SKIN_CONCERNS = [
  "Acne", "Hyperpigmentation", "Dark spots", "Fine lines / wrinkles",
  "Large pores", "Dull skin", "Rosacea", "Sensitivity"
];

const SKIN_TYPES = ["Oily", "Dry", "Combination", "Sensitive", "Acne-Prone", "Aging"];

const HealthHistoryForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const memberId = searchParams.get("member_id");
  const transactionId = searchParams.get("transaction_id");
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    health_conditions: [],
    allergies: "",
    under_doctor_care: false,
    doctor_care_explanation: "",
    medications: [],
    other_medications: "",
    skin_type: "",
    current_skincare_products: "",
    had_facial_before: false,
    recent_treatments: [],
    skin_concerns: [],
    other_skin_concerns: "",
    waxing_areas: "",
    accutane_last_12_months: false,
    using_retin_a: false,
    recent_sunburn_laser_peel: false,
    circulation_issues: false,
    consent_signature: "",
    consent_date: new Date().toISOString().split('T')[0]
  });

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_signature) {
      toast.error("Please sign the consent form");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/health-form`, {
        member_id: memberId || "guest",
        transaction_id: transactionId,
        ...formData
      });
      
      setIsSubmitted(true);
      toast.success("Health form submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 aurora-bg">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card rounded-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <h2 className="font-italiana text-2xl text-white mb-2">Form Submitted!</h2>
          <p className="text-white/60 mb-6">
            Thank you for completing your health history form. We'll review this before your appointment.
          </p>
          <Button onClick={() => navigate("/")} className="btn-primary rounded-full px-8">
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Health History Form"
        description="Complete your health history form for Glamour Body services"
      />
      
      <div className="min-h-screen py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-italiana text-4xl text-white mb-2">Health History Form</h1>
            <p className="text-white/60">Please complete this form before your appointment</p>
          </motion.div>
          
          <Card className="glass-card border-white/10">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Health Conditions */}
                <div>
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <AlertCircle size={18} className="text-cyan-400" />
                    Health Conditions
                  </h3>
                  <p className="text-white/60 text-sm mb-4">Do you currently have or have you had any of the following?</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {HEALTH_CONDITIONS.map((condition) => (
                      <label key={condition} className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                        <Checkbox
                          checked={formData.health_conditions.includes(condition)}
                          onCheckedChange={() => handleCheckboxChange("health_conditions", condition)}
                        />
                        {condition}
                      </label>
                    ))}
                    <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                      <Checkbox
                        checked={formData.health_conditions.includes("None")}
                        onCheckedChange={() => handleCheckboxChange("health_conditions", "None")}
                      />
                      None
                    </label>
                  </div>
                  
                  <div className="mt-4">
                    <Label className="text-white/70 text-sm">Allergies (please list)</Label>
                    <Input
                      value={formData.allergies}
                      onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                      className="input-dark rounded-lg mt-1"
                      placeholder="List any allergies..."
                    />
                  </div>
                </div>
                
                {/* Doctor's Care */}
                <div className="space-y-4">
                  <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                    <Checkbox
                      checked={formData.under_doctor_care}
                      onCheckedChange={(checked) => setFormData({...formData, under_doctor_care: checked})}
                    />
                    Are you currently under a doctor's care?
                  </label>
                  {formData.under_doctor_care && (
                    <Input
                      value={formData.doctor_care_explanation}
                      onChange={(e) => setFormData({...formData, doctor_care_explanation: e.target.value})}
                      className="input-dark rounded-lg"
                      placeholder="Please explain..."
                    />
                  )}
                </div>
                
                {/* Medications */}
                <div>
                  <h3 className="text-white font-semibold mb-4">Current Medications</h3>
                  <p className="text-white/60 text-sm mb-4">Are you taking any medications (oral or topical)?</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {MEDICATIONS.map((med) => (
                      <label key={med} className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                        <Checkbox
                          checked={formData.medications.includes(med)}
                          onCheckedChange={() => handleCheckboxChange("medications", med)}
                        />
                        {med}
                      </label>
                    ))}
                  </div>
                  <Input
                    value={formData.other_medications}
                    onChange={(e) => setFormData({...formData, other_medications: e.target.value})}
                    className="input-dark rounded-lg mt-3"
                    placeholder="Other medications..."
                  />
                </div>
                
                {/* Skin Care Information */}
                <div className="border-t border-white/10 pt-8">
                  <h3 className="text-white font-semibold mb-4">Skin Care Information (Facials)</h3>
                  
                  <div className="mb-6">
                    <Label className="text-white/70 text-sm mb-3 block">Skin Type</Label>
                    <RadioGroup
                      value={formData.skin_type}
                      onValueChange={(value) => setFormData({...formData, skin_type: value})}
                      className="flex flex-wrap gap-4"
                    >
                      {SKIN_TYPES.map((type) => (
                        <label key={type} className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                          <RadioGroupItem value={type} />
                          {type}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  
                  <div className="mb-6">
                    <Label className="text-white/70 text-sm">Current Skincare Products Used</Label>
                    <Textarea
                      value={formData.current_skincare_products}
                      onChange={(e) => setFormData({...formData, current_skincare_products: e.target.value})}
                      className="input-dark rounded-lg mt-1"
                      placeholder="List your current skincare products..."
                      rows={3}
                    />
                  </div>
                  
                  <label className="flex items-center gap-2 text-white/70 mb-6 cursor-pointer">
                    <Checkbox
                      checked={formData.had_facial_before}
                      onCheckedChange={(checked) => setFormData({...formData, had_facial_before: checked})}
                    />
                    Have you had a facial before?
                  </label>
                  
                  <div className="mb-6">
                    <p className="text-white/70 text-sm mb-3">Have you used any of the following in the last 7 days?</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {RECENT_TREATMENTS.map((treatment) => (
                        <label key={treatment} className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                          <Checkbox
                            checked={formData.recent_treatments.includes(treatment)}
                            onCheckedChange={() => handleCheckboxChange("recent_treatments", treatment)}
                          />
                          {treatment}
                        </label>
                      ))}
                      <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                        <Checkbox
                          checked={formData.recent_treatments.includes("None")}
                          onCheckedChange={() => handleCheckboxChange("recent_treatments", "None")}
                        />
                        None
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-white/70 text-sm mb-3">Skin concerns (check all that apply)</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {SKIN_CONCERNS.map((concern) => (
                        <label key={concern} className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                          <Checkbox
                            checked={formData.skin_concerns.includes(concern)}
                            onCheckedChange={() => handleCheckboxChange("skin_concerns", concern)}
                          />
                          {concern}
                        </label>
                      ))}
                    </div>
                    <Input
                      value={formData.other_skin_concerns}
                      onChange={(e) => setFormData({...formData, other_skin_concerns: e.target.value})}
                      className="input-dark rounded-lg mt-3"
                      placeholder="Other concerns..."
                    />
                  </div>
                </div>
                
                {/* Waxing Information */}
                <div className="border-t border-white/10 pt-8">
                  <h3 className="text-white font-semibold mb-4">Waxing Information</h3>
                  
                  <div className="mb-6">
                    <Label className="text-white/70 text-sm">Area(s) being waxed today</Label>
                    <Input
                      value={formData.waxing_areas}
                      onChange={(e) => setFormData({...formData, waxing_areas: e.target.value})}
                      className="input-dark rounded-lg mt-1"
                      placeholder="e.g., Brazilian, Underarms, etc."
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                      <Checkbox
                        checked={formData.accutane_last_12_months}
                        onCheckedChange={(checked) => setFormData({...formData, accutane_last_12_months: checked})}
                      />
                      Have you used Accutane in the last 12 months?
                    </label>
                    <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                      <Checkbox
                        checked={formData.using_retin_a}
                        onCheckedChange={(checked) => setFormData({...formData, using_retin_a: checked})}
                      />
                      Are you using Retin-A or any prescription exfoliating product?
                    </label>
                    <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                      <Checkbox
                        checked={formData.recent_sunburn_laser_peel}
                        onCheckedChange={(checked) => setFormData({...formData, recent_sunburn_laser_peel: checked})}
                      />
                      Have you had any recent sunburn, laser treatments, or chemical peels?
                    </label>
                    <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                      <Checkbox
                        checked={formData.circulation_issues}
                        onCheckedChange={(checked) => setFormData({...formData, circulation_issues: checked})}
                      />
                      Do you have diabetes, varicose veins, or circulation issues in the area being waxed?
                    </label>
                  </div>
                </div>
                
                {/* Consent */}
                <div className="border-t border-white/10 pt-8">
                  <h3 className="text-white font-semibold mb-4">Consent & Signature</h3>
                  <p className="text-white/60 text-sm mb-4">
                    I confirm that the information provided above is accurate and complete to the best of my knowledge. 
                    I understand that withholding information may result in complications during treatment.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70 text-sm">Signature (Type your full name) *</Label>
                      <Input
                        value={formData.consent_signature}
                        onChange={(e) => setFormData({...formData, consent_signature: e.target.value})}
                        className="input-dark rounded-lg mt-1"
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-sm">Date</Label>
                      <Input
                        type="date"
                        value={formData.consent_date}
                        onChange={(e) => setFormData({...formData, consent_date: e.target.value})}
                        className="input-dark rounded-lg mt-1"
                      />
                    </div>
                  </div>
                </div>
                
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary rounded-full py-6 text-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Health Form"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default HealthHistoryForm;
